// Auto-extracted tab: vendors
import React, { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * VendorsTab - extracted from app.jsx
 * Original lines: 8570-8766
 * 
 * TODO: This component needs props passed from AppContent:
 * - darkMode, scenarios, thresholds, etc.
 * - All the state and handlers it uses
 */
export function VendorsTab(props) {
  // Original render content (needs refactoring to use props):
  return (
    <>
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className={`p-4 rounded-xl ${darkMode ? "bg-slate-800/50 border border-slate-700" : "bg-white border border-slate-200"}`}>
                <div className={`text-2xl font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>{vendors.length}</div>
                <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Total Vendors</div>
              </div>
              <div className={`p-4 rounded-xl ${darkMode ? "bg-red-900/30 border border-red-800" : "bg-red-50 border border-red-200"}`}>
                <div className={`text-2xl font-bold ${darkMode ? "text-red-400" : "text-red-700"}`}>
                  {vendors.filter(v => v.risk_tier === 'critical').length}
                </div>
                <div className={`text-sm ${darkMode ? "text-red-300" : "text-red-600"}`}>Critical Risk</div>
              </div>
              <div className={`p-4 rounded-xl ${darkMode ? "bg-amber-900/30 border border-amber-800" : "bg-amber-50 border border-amber-200"}`}>
                <div className={`text-2xl font-bold ${darkMode ? "text-amber-400" : "text-amber-700"}`}>
                  {vendorAssessments.filter(a => a.status === 'sent' || a.status === 'in_progress').length}
                </div>
                <div className={`text-sm ${darkMode ? "text-amber-300" : "text-amber-600"}`}>Assessments Pending</div>
              </div>
              <div className={`p-4 rounded-xl ${darkMode ? "bg-blue-900/30 border border-blue-800" : "bg-blue-50 border border-blue-200"}`}>
                <div className={`text-2xl font-bold ${darkMode ? "text-blue-400" : "text-blue-700"}`}>
                  {vendors.filter(v => v.contract_end_date && new Date(v.contract_end_date) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)).length}
                </div>
                <div className={`text-sm ${darkMode ? "text-blue-300" : "text-blue-600"}`}>Contracts Expiring (90d)</div>
              </div>
            </div>
            
            {/* Vendor List */}
            <Card 
              darkMode={darkMode} 
              title="Vendor Registry" 
              subtitle={`${vendors.length} third-party vendors`}
              actions={
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={loadVendorData} disabled={vendorLoading}>
                    <Icons.Refresh className={vendorLoading ? "animate-spin" : ""} />
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => setVendorModal({ open: true, vendor: null })}>
                    <Icons.Plus /> Add Vendor
                  </Button>
                </div>
              }
            >
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-4">
                <select 
                  value={vendorFilter.type} 
                  onChange={e => setVendorFilter({...vendorFilter, type: e.target.value})}
                  className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-300 text-slate-900"} border`}
                >
                  <option value="all">All Types</option>
                  <option value="critical">Critical</option>
                  <option value="significant">Significant</option>
                  <option value="limited">Limited</option>
                  <option value="minimal">Minimal</option>
                </select>
                <select 
                  value={vendorFilter.tier} 
                  onChange={e => setVendorFilter({...vendorFilter, tier: e.target.value})}
                  className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-300 text-slate-900"} border`}
                >
                  <option value="all">All Risk Tiers</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                <select 
                  value={vendorFilter.status} 
                  onChange={e => setVendorFilter({...vendorFilter, status: e.target.value})}
                  className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-300 text-slate-900"} border`}
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="under_review">Under Review</option>
                  <option value="remediation">Remediation</option>
                  <option value="onboarding">Onboarding</option>
                </select>
              </div>
              
              {/* Vendor Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className={darkMode ? "bg-slate-800/50" : "bg-gradient-to-r from-slate-50 to-slate-100/80"}>
                    <tr>
                      <th className={`text-left py-3.5 px-4 font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Vendor</th>
                      <th className={`text-left py-3.5 px-4 font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Type</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Risk Tier</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Status</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Contract End</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Next Assessment</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendors
                      .filter(v => vendorFilter.type === 'all' || v.vendor_type === vendorFilter.type)
                      .filter(v => vendorFilter.tier === 'all' || v.risk_tier === vendorFilter.tier)
                      .filter(v => vendorFilter.status === 'all' || v.status === vendorFilter.status)
                      .map(vendor => (
                      <tr key={vendor.id} className={`${darkMode ? "border-slate-700/50 hover:bg-slate-800/50" : "border-slate-100 hover:bg-slate-50"} border-b cursor-pointer`}>
                        <td className="py-3 px-2">
                          <div className={`font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{vendor.name}</div>
                          <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>{vendor.service_category}</div>
                        </td>
                        <td className="py-3 px-2">
                          <Badge variant={vendor.vendor_type === 'critical' ? 'critical' : vendor.vendor_type === 'significant' ? 'high' : 'medium'}>
                            {vendor.vendor_type}
                          </Badge>
                        </td>
                        <td className="py-3 px-2">
                          <Badge variant={vendor.risk_tier === 'critical' ? 'critical' : vendor.risk_tier === 'high' ? 'high' : vendor.risk_tier === 'medium' ? 'medium' : 'low'}>
                            {vendor.risk_tier || 'Not Assessed'}
                          </Badge>
                        </td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            vendor.status === 'active' ? (darkMode ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-700') :
                            vendor.status === 'under_review' ? (darkMode ? 'bg-amber-900/50 text-amber-400' : 'bg-amber-100 text-amber-700') :
                            (darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600')
                          }`}>
                            {vendor.status}
                          </span>
                        </td>
                        <td className={`py-3 px-2 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>
                          {vendor.contract_end_date ? new Date(vendor.contract_end_date).toLocaleDateString() : '-'}
                        </td>
                        <td className={`py-3 px-2 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>
                          {vendor.next_assessment_date ? new Date(vendor.next_assessment_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="xs" onClick={() => setVendorDetailModal({ open: true, vendor })}>
                              <Icons.Eye />
                            </Button>
                            <Button variant="ghost" size="xs" onClick={() => openVendorEdit(vendor)}>
                              <Icons.Edit />
                            </Button>
                            <Button variant="ghost" size="xs" onClick={() => setAssessmentModal({ open: true, vendor, assessment: null })}>
                              <Icons.Send />
                            </Button>
                            <Button variant="ghost" size="xs" onClick={() => openDeleteModal("vendor", vendor)}>
                              <Icons.Trash className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {vendors.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-4">
                          <EducationalEmptyState
                            title="No vendors documented yet"
                            examinerSays="Who processes your payments? Who hosts your core system? Show me your vendor inventory and how you assess their risks."
                            example="Jack Henry & Associates - Core Banking Provider (Critical). SOC 2 Type II certified. Annual assessment completed 2024-01-15. Contract expires 2026-12-31."
                            templates={[
                              { name: "Core Banking Provider", type: "core_banking" },
                              { name: "Payment Processor", type: "payments" },
                              { name: "Cloud/IT Provider", type: "cloud" },
                              { name: "Compliance Software", type: "compliance" },
                            ]}
                            onAddBlank={() => setVendorModal({ open: true, vendor: null })}
                            onSelectTemplate={(t) => {
                              setNewVendorTemplate(t.type);
                              setVendorModal({ open: true, vendor: null });
                            }}
                            darkMode={darkMode}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
            
            {/* Open Vendor Issues */}
            {vendorIssues.filter(i => i.status !== 'closed').length > 0 && (
              <Card darkMode={darkMode} title="Open Vendor Issues" subtitle={`${vendorIssues.filter(i => i.status !== 'closed').length} issues requiring attention`}>
                <div className="space-y-2">
                  {vendorIssues.filter(i => i.status !== 'closed').slice(0, 5).map(issue => (
                    <div key={issue.id} className={`p-3 rounded-lg ${darkMode ? "bg-slate-800/50" : "bg-slate-50"} flex items-center justify-between`}>
                      <div>
                        <div className={`font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{issue.title}</div>
                        <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                          {vendors.find(v => v.id === issue.vendor_id)?.name} • Due: {issue.due_date ? new Date(issue.due_date).toLocaleDateString() : 'Not set'}
                        </div>
                      </div>
                      <Badge variant={issue.severity}>{issue.severity}</Badge>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
    </>
  );
}

export default VendorsTab;
